import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Layers
} from "lucide-react";
import * as ownerApi from "../../api/payroll/salaryComponentApi";
import * as portalApi from "../../api/payroll/salaryComponentPortalApi";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const TYPE_OPTIONS = [
  { label: "EARNING", value: "EARNING" },
  { label: "DEDUCTION", value: "DEDUCTION" },
];

const CALC_OPTIONS = [
  { label: "FLAT (amount)", value: "FLAT" },
  { label: "PERCENTAGE_OF_BASIC", value: "PERCENTAGE_OF_BASIC" },
];

export default function SalaryComponentForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/salary-components" : "/salary-components";
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    type: "EARNING",
    calculationType: "FLAT",
    defaultValue: 0,
    isStatutory: false,
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAllSalaryComponents();
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            name: found.name || "",
            type: found.type || "EARNING",
            calculationType: found.calculationType || "FLAT",
            defaultValue: found.defaultValue ?? 0,
            isStatutory: !!found.isStatutory,
          });
        } else {
          toast.error("Salary component not found");
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, isPortal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        calculationType: form.calculationType,
        defaultValue: Number(form.defaultValue) || 0,
        isStatutory: !!form.isStatutory,
      };
      if (!payload.name) {
        toast.error("Name is required");
        setLoading(false);
        return;
      }
      if (isEdit) {
        await api.updateSalaryComponent(id, payload);
        toast.success("Updated");
      } else {
        await api.createSalaryComponent(payload);
        toast.success("Created");
      }
      setTimeout(() => navigate(basePath), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          onClick={() => navigate(basePath)}
          className="mb-2 -ml-2"
        >
          Back to Salary Components
        </Button>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <Layers className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          {isEdit ? "Edit Salary Component" : "Create Salary Component"}
        </h1>
      </div>

      
      

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder="Basic / HRA / PF"
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Type"
                options={TYPE_OPTIONS}
                value={form.type}
                onChange={(val) => setForm((prev) => ({ ...prev, type: val }))}
              />

              <Select
                label="Calculation"
                options={CALC_OPTIONS}
                value={form.calculationType}
                onChange={(val) => setForm((prev) => ({ ...prev, calculationType: val }))}
              />
            </div>

            <Input
              label={`Default value ${form.calculationType === "PERCENTAGE_OF_BASIC" ? "(%)" : "(amount)"}`}
              name="defaultValue"
              type="number"
              min="0"
              step="0.01"
              value={form.defaultValue}
              onChange={handleChange}
            />

            <Checkbox
              id="isStatutory"
              name="isStatutory"
              checked={form.isStatutory}
              onChange={handleChange}
              label="Statutory (flag only)"
            />

            <div className="flex gap-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <Button
                type="submit"
                variant="primary"
                isLoading={loading}
                leftIcon={<Save className="h-4 w-4" />}
              >
                {isEdit ? "Update" : "Create"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(basePath)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}